<template>
  <div>
    <v-alert v-if="showUpdateOfferListAlert" dense class="offer-list-alert" icon="mdi-update" type="info" transition="fade-transition">
      <v-row dense>
        <v-col cols="6" class="grow text-center">
          <span class="font-weight-bold">New Offers Available!</span>
        </v-col>
        <v-col cols="6" class="shrink">
          <v-btn small outlined class="float-right" @click="fetchOffers(true)">Update</v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <v-row>
      <v-col cols="12" class="text-center">
        <v-row align="center" justify="space-around">
          <LfDealSurveyCard
            v-for="offer in offerList"
            :id="offer.offer_id"
            :key="offer.offer_id"
            v-observe-visibility="{
              once: true,
              intersection: {
                threshold: 0.9,
              },
              callback: (isVisible, entry) => visibilityChanged(isVisible, entry, offer),
            }"
            :business-brand="offer.brand"
            :image-url="offer.offer_image_url"
            :deal-title="offer.offer_summary"
            :expiration-date="offer.expiration_date"
            :vouchers-left="offer.vouchers_left"
            :distance="offer.distance"
            :city="offer.city"
            :event-date="getEventDate(offer.event_date_option)"
            class="mb-7"
            @deal-selected="onDealSelected"
          />
        </v-row>

        <app-spinner v-if="loading" class="mt-5" />

        <span v-if="!loading && offerList.length === 0" class="headline">No offers for this location</span>

        <div v-if="!loading && !shouldLoadMore">
          <v-row>
            <v-col v-if="searchLatitude && searchLongitude" cols="12">
              <h3 class="font-weight-light">That's all the nearby offers</h3>
              <h3 class="font-weight-light">Check out more offers in other cities!</h3>
            </v-col>
            <v-col v-else cols="12">
              <h3 class="font-weight-light">That's all the web offers</h3>
              <h3 class="font-weight-light">Check out our local offers!</h3>
            </v-col>
          </v-row>
          <CityQuickLinks
            :hide-web-link="!searchLatitude && !searchLongitude"
            @city-quick-link-web-clicked="onWebQuickLinkClicked"
            @city-quick-link-city-clicked="onCityQuickLinkClicked"
          />
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import { LfDealSurveyCard } from 'lf-common'

import CityQuickLinks from '@/components/CityQuickLinks.vue'

import dayjs from 'dayjs'

export default Vue.extend({
  name: 'OfferList',

  components: {
    LfDealSurveyCard,
    CityQuickLinks,
  },

  data: () => ({
    offerViewedAuditQueue: [],
    offerViewedAuditTimeout: null,
  }),

  computed: {
    ...mapGetters('offers', [
      'loading',
      'getOffer',
      'offerList',
      'shouldLoadMore',
      'scrollPosition',
      'searchLatitude',
      'searchLongitude',
      'showUpdateOfferListAlert',
    ]),
  },

  watch: {
    offerList: function (event) {
      this.$nextTick(() => {
        this.offerViewedAuditTimeout = setTimeout(() => {
          this.sendOfferAuditViewEvent()
        }, 1000)
      })
    },
  },

  mounted: function () {
    if (this.scrollPosition) {
      this.$nextTick(() => {
        window.scrollTo(0, this.scrollPosition)
      })
    }

    window.addEventListener('scroll', this.getMoreOffers)
    window.addEventListener('scroll', () => {
      window.clearTimeout(this.offerViewedAuditTimeout)
      this.offerViewedAuditTimeout = setTimeout(() => {
        this.sendOfferAuditViewEvent()
      }, 300)
    })
  },

  beforeDestroy: function () {
    window.removeEventListener('scroll', this.getMoreOffers)
  },

  methods: {
    ...mapActions('offers', ['fetchOffers', 'setScrollPosition', 'sendOfferViewedAuditEvent']),
    onCityQuickLinkClicked(quickLink) {
      this.$emit('offer-list-city-quick-link-clicked', quickLink)
    },
    onWebQuickLinkClicked() {
      this.$emit('offer-list-web-quick-link-clicked')
    },
    onDealSelected(offerId) {
      const offer = this.getOffer(offerId)

      if (this.searchLongitude && this.searchLongitude && offer.address_id) {
        this.$router
          .push({ path: `/claimOffer/${offerId}/${offer.address_id ? offer.address_id : ''}/${this.searchLongitude}/${this.searchLatitude}` })
          .catch((error) => {
            console.warn('Navigation Duplicated: City & State Selected')
          })
      } else {
        this.$router.push({ path: `/claimOffer/${offerId}` }).catch((error) => {
          console.warn('Navigation Duplicated: City & State Selected')
        })
      }
    },
    getMoreOffers() {
      this.setScrollPosition(document.body.scrollTop || document.documentElement.scrollTop)

      if (this.loading || !this.shouldLoadMore) return

      const bottomOfWindow =
        Math.ceil(document.documentElement.scrollTop) + Math.ceil(window.innerHeight) + 5 >= Math.floor(document.documentElement.offsetHeight)

      if (bottomOfWindow) {
        this.fetchOffers()
      }
    },
    visibilityChanged(isVisible, entry, offer) {
      if (!isVisible) return

      this.offerViewedAuditQueue.push(offer.id)
    },
    sendOfferAuditViewEvent() {
      if (this.offerViewedAuditQueue.length === 0) return

      this.sendOfferViewedAuditEvent(this.offerViewedAuditQueue)
      this.offerViewedAuditQueue = []
    },
    getEventDate(options) {
      if (!options || options.length <= 0) return null
      const firstDate = dayjs(
        options.reduce((p, v) => {
          return dayjs(p.eventDate).isBefore(dayjs(v.eventDate)) ? p : v
        })?.eventDate
      ).format('MMM DD')
      const lastDate = dayjs(
        options.reduce((p, v) => {
          return !dayjs(p.eventDate).isBefore(dayjs(v.eventDate)) ? p : v
        })?.eventDate
      ).format('MMM DD')
      if (lastDate != firstDate) {
        return `${firstDate} - ${lastDate}`
      }
      return firstDate
    },
  },
})
</script>

<style scoped>
.offer-list-alert {
  width: 100%;
  left: 0;
  bottom: 40px;
  z-index: 3;
  border-radius: 0;
  position: fixed;
}
</style>
